import { useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, getErrorMessage } from "@/lib/api";

export default function AdminUpload() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/intake/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Processed ${data.total} rows`);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="upload-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
          <FileUp className="h-6 w-6 text-primary" /> Upload Data
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Upload a CSV with columns <code className="text-xs bg-muted px-1 rounded">name, mobile, source</code>.
          New numbers create a fresh unassigned lead; numbers matching an existing candidate go to the Dispute tab.
        </p>
      </div>

      <Card className="rounded-xl">
        <CardHeader><CardTitle className="font-heading text-lg">CSV upload</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <input type="file" accept=".csv,text/csv" data-testid="csv-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2 file:text-white file:font-medium" />
          <Button onClick={submit} disabled={busy || !file} className="rounded-lg gap-2" data-testid="csv-upload-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Process CSV
          </Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="rounded-xl" data-testid="upload-summary">
          <CardHeader><CardTitle className="font-heading text-lg">Result</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                <p className="text-2xl font-semibold text-emerald-700" data-testid="summary-created">{result.created}</p>
                <p className="text-xs text-emerald-700">Created (Fresh Leads)</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                <p className="text-2xl font-semibold text-amber-700" data-testid="summary-disputed">{result.disputed}</p>
                <p className="text-xs text-amber-700">Sent to Dispute</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                <p className="text-2xl font-semibold text-red-700" data-testid="summary-failed">{result.failed}</p>
                <p className="text-xs text-red-700">Failed</p>
              </div>
            </div>
            {result.failures?.length > 0 && (
              <div className="text-sm">
                <p className="font-medium mb-1">Failed rows:</p>
                <ul className="space-y-0.5 text-muted-foreground">
                  {result.failures.map((f, i) => (
                    <li key={i}>Row {f.row}: {f.name || "(no name)"} / {f.mobile || "(no mobile)"} — {f.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
